import React from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, TableRow, Chip, 
    Typography, Box, Tooltip, IconButton, alpha, Pagination, Card, FormControlLabel, Switch, Divider,
    Menu, MenuItem, ListItemIcon
} from '@mui/material';
import { 
    InfoOutlined as InfoIcon, Person as PersonIcon, 
    Delete as DeleteIcon, Restore as RestoreIcon, Visibility as VisibilityIcon,
    WarningAmber as WarningIcon, CheckCircleOutline as CheckCircleIcon,
    FactCheckOutlined as FactCheckIcon,
    SupportAgentOutlined as SupportAgentIcon,
    LockOutlined as LockIcon,
    Download as DownloadIcon,
    ArrowBackIosNew as ArrowLeftIcon,
    ArrowForwardIos as ArrowRightIcon,
    Share as ShareIcon,
    WhatsApp as WhatsAppIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { useDispatch, useSelector } from 'react-redux';

import { SinDatos } from '../../../../commons/generics/SinDatos';
import { ClampedText } from '../../../../commons/generics/ClampedText';
import { AudioPlayer } from './AudioPlayer';
import {  GenericTableHead, HeadCell } from '../../../../commons/generics/GenericTableHead';
import { StatusChip } from '../../../../commons/generics/StatusChip';
import { ModalConfirmDeactivation } from '../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../components/modals/ModalConfirmRestore';
import { storeDeleteQualification, storeRestoreQualification, getQualificationsByPagination } from '../../../../store/ratings/thunks';
import { formatDateToYmd, formatDateToYmdNumeric, hasPemissions } from '../../../../commons/utils/helpers';
import { useTableState } from '../../../../hooks/useTableState';
import { ShareTicket } from './ShareTicket';

const getAnswerColor = (metricType: string, score: any) => {
    const numScore = Number(score);
    if (isNaN(numScore)) return '#ffffff';
    
    if (numScore >= 4) return '#5DCC0A';
    if (numScore === 3) return '#FFB400';
    return '#FF4C51';
};

const headCells: HeadCell[] = [
    { id: 'clientName',  label: 'Cliente', sortable: false },
    { id: 'comments',  label: 'Comentarios', sortable: false },
    { id: 'surveyName',  label: 'Encuesta', sortable: false },
    { id: 'origin',  label: 'Origen', sortable: false },
    { id: 'deviceName',  label: 'Dispositivo', sortable: false },
    { id: 'progress',  label: 'Progreso', sortable: false },
    { id: 'averageScore',  label: 'Promedio', sortable: false },
    { id: 'metrics',  label: 'Métricas', sortable: false },
    { id: 'agentName',   label: 'Agente', sortable: false },
    { id: 'createdAt',   label: 'Fecha', sortable: false },
    { id: 'audio',   label: 'Audio', sortable: false },
    { id: 'status',  label: 'Estado', sortable: false },
    { id: 'actions',   label: 'Acciones', sortable: false },
];

export const TableRatingsQualifications = ({
    filters,
    handleViewCase,
    onStatusChange
}: any) => {

    const dispatch = useDispatch<any>();
    
    const { qualifications_list, qualifications_stats, has_discrepancie, completion_percentage, areas_qualifications } = useSelector((state: any) => state.ratingsDevices);
    
    const {
        dataTableList,
        canView, setCanView,
        canDelete, setCanDelete,
        canRestore, setCanRestore,
        checked,
        ResponseComponents,
        handleResponse,
        handleChangePage,
        handleChangeRowsPerPage,
        handleSwitchChange,
        updatePaginationFromList,
        changePaginationPage: changePaginationPageHook,
        page,
        rowsPerPage,
        pagePagination,
        openModalDeactivation, setOpenModalDeactivation,
        openModalRestore, setOpenModalRestore,
        openElem, setOpenElem,
        isServerSide,
        itemCountPagination
    } = useTableState('showInactivesQualifications');

    const [hasPermission, setHasPermission] = React.useState<boolean>(true);
    const [contextMenu, setContextMenu] = React.useState<{ mouseX: number; mouseY: number; row: any } | null>(null);
    const ticketRef = React.useRef<HTMLElement>(null);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const [isTableVisible, setIsTableVisible] = React.useState(false);

    const observerRef = React.useRef<IntersectionObserver | null>(null);

    const handleTableRef = React.useCallback((node: HTMLDivElement) => {
        tableContainerRef.current = node;
        if (observerRef.current) observerRef.current.disconnect();
        if (node) {
            observerRef.current = new IntersectionObserver(
                ([entry]) => {
                    setIsTableVisible(entry.isIntersecting);
                },
                { threshold: 0, rootMargin: '-35% 0px -35% 0px' }
            );
            observerRef.current.observe(node);
        }
    }, []);

    const handleScrollTable = (direction: 'left' | 'right') => {
        if (tableContainerRef.current) {
            const scrollAmount = 300;
            tableContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleContextMenu = (event: React.MouseEvent, row: any) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                      mouseX: event.clientX + 2,
                      mouseY: event.clientY - 6,
                      row,
                  }
                : null,
        );
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const generateImageBlob = async (): Promise<Blob | null> => {
        if (!ticketRef.current) return null;
        try {
            const canvas = await html2canvas(ticketRef.current, {
                scale: 2,
                backgroundColor: null,
                useCORS: true,
            });
            return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        } catch (error) {
            console.error('Error al generar la imagen', error);
            return null;
        }
    };

    const handleDownloadImage = async () => {
        const blob = await generateImageBlob();
        if (!blob || !contextMenu?.row) return;
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Calificacion_${contextMenu.row.clientName?.replace(/\s+/g, '_') || 'Cliente'}_${formatDateToYmd(contextMenu.row.createdAt)}.png`;
        link.click();
        URL.revokeObjectURL(url);
        handleCloseContextMenu();
    };

    const handleShareWhatsAppApp = async () => {
        const blob = await generateImageBlob();
        if (!blob) return;
        
        const file = new File([blob], 'calificacion.png', { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try { await navigator.share({ files: [file], title: 'Calificación' }); } catch (error) { console.error(error); }
        } else {
            // Si no hay Web Share API (ej. desktop viejo), al menos abrir WhatsApp desktop/web
            window.open('https://wa.me/?text=Mira%20esta%20calificaci%C3%B3n', '_blank');
        }
        handleCloseContextMenu();
    };

    const handleShareWhatsAppWeb = async () => {
        const blob = await generateImageBlob();
        if (!blob) return;
        
        try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            alert('La imagen se ha copiado al portapapeles. Ya puedes pegarla en WhatsApp Web (Ctrl+V).');
        } catch (error) {
            alert('Tu navegador no soporta copiar imágenes directamente. La imagen se descargará automáticamente para que puedas enviarla manualmente.');
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'calificacion.png';
            link.click();
        }
        window.open('https://web.whatsapp.com/', '_blank');
        handleCloseContextMenu();
    };

    const handleShareEmail = async () => {
        const blob = await generateImageBlob();
        if (!blob) return;
        
        const file = new File([blob], 'calificacion.png', { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try { await navigator.share({ files: [file], title: 'Calificación' }); } catch (error) { console.error(error); }
        } else {
            try {
                await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                alert('La imagen se ha copiado al portapapeles. Abre tu correo y pégala (Ctrl+V) en el mensaje.');
            } catch (error) {
                alert('Tu navegador no soporta copiar imágenes. La imagen se descargará automáticamente para que puedas adjuntarla.');
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'calificacion.png';
                link.click();
            }
            window.open('mailto:?subject=Calificación de cliente', '_self');
        }
        handleCloseContextMenu();
    };

    React.useEffect(() => {
        if (qualifications_list) {
            updatePaginationFromList(qualifications_list);
        }
    }, [qualifications_list]);

    React.useEffect(() => {
        checkPermissions();
    }, []);

    const checkPermissions = async () => {


        const view = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.gestion.calificaciones', 'view');
        if (!view) setHasPermission(false);
        else setCanView(true);

        const del = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.gestion.calificaciones', 'delete');
        if (del) setCanDelete(true);

        const rest = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.gestion.calificaciones', 'restore');
        if (rest) setCanRestore(true);
    };

    const handleDelete = (qualification: any) => {
        setOpenElem(qualification);
        setOpenModalDeactivation(true);
    };

    const confirmDeactivate = async () => {
        if (!openElem) return;
        const response = await dispatch(storeDeleteQualification(openElem.id, filters));
        await handleResponse(response, 'desactivar calificación', true);
        setOpenModalDeactivation(false);
    };

    const handleRestore = (qualification: any) => {
        setOpenElem(qualification);
        setOpenModalRestore(true);
    };

    const confirmRestore = async () => {
        if (!openElem) return;
        const response = await dispatch(storeRestoreQualification(openElem.id, filters));
        await handleResponse(response, 'restaurar calificación', true);
        setOpenModalRestore(false);
    };

    const getScoreColor = (score: number) => {
        if (score >= 4.5) return '#5DCC0A'; 
        if (score >= 3.5) return '#FFB400'; 
        if (score >= 2.5) return '#FF8A00'; 
        if (score >= 1.5) return '#FF4C51'; 
        return '#FF4C51'; 
    };

    const getNPSChipProps = (val: any) => {
        if (!val) {
            return { label: 'Sin Datos', color: '#888', bgcolor: '#f5f5f5', borderColor: '#ccc', tooltip: 'Sin datos' };
        }
        if (val.metric === 'NPS' || val.name === 'NPS' || val.type === 'Promotor' || val.type === 'Pasivo' || val.type === 'Detractor') {
            if (val.type === 'Promotor') {
                return { label: 'Promotor', color: '#4ba804', bgcolor: alpha('#5DCC0A', 0.1), borderColor: alpha('#5DCC0A', 0.3), tooltip: 'Clientes altamente satisfechos' };
            }
            if (val.type === 'Pasivo') {
                return { label: 'Pasivo', color: '#d39600', bgcolor: alpha('#FFB400', 0.1), borderColor: alpha('#FFB400', 0.3), tooltip: 'Clientes satisfechos pero vulnerables' };
            }
            if (val.type === 'Detractor') {
                return { label: 'Detractor', color: '#e53e3e', bgcolor: alpha('#FF4C51', 0.1), borderColor: alpha('#FF4C51', 0.3), tooltip: 'Clientes con baja probabilidad de recomendación' };
            }
            return { label: `NPS: ${val.score}%`, color: '#4ba804', bgcolor: alpha('#5DCC0A', 0.1), borderColor: alpha('#5DCC0A', 0.3), tooltip: `NPS: ${val.score}%` };
        }
        if (val.metric === 'CSAT' || val.name === 'CSAT' || (val.metricType === 'CSAT')) {
            return { label: `CSAT: ${val.score}%`, color: '#3f51b5', bgcolor: alpha('#3f51b5', 0.1), borderColor: alpha('#3f51b5', 0.3), tooltip: 'Customer Satisfaction Score' };
        }
        if (val.metric === 'CES' || val.name === 'CES' || (val.metricType === 'CES')) {
            return { label: `CES: ${val.score}`, color: '#ff9800', bgcolor: alpha('#ff9800', 0.1), borderColor: alpha('#ff9800', 0.3), tooltip: 'Customer Effort Score' };
        }
        return { label: val.metric || val.name || val.type || `Score: ${val.score}`, color: '#888', bgcolor: '#f5f5f5', borderColor: '#ccc', tooltip: 'Métrica' };
    };

    // ESTO SE USABA ANTES PARA MOSTRAR ACTIOS O INACTIVOS PERO YA NO SE USA SE REFACTORIZA DESPUES 
    // DEJARLO ASI MIEINTRAS TANTO, AHORA VIENEN ACTIVOS O INACTIVOS DESDE EL BACKEND
    const filteredList = dataTableList.filter((row: any) => checked || row.status);
    const totalFilteredCount = isServerSide ? (itemCountPagination * rowsPerPage) : filteredList.length; // Approximate count for fallback
    const calculatedPageCount = isServerSide ? itemCountPagination : Math.ceil(totalFilteredCount / rowsPerPage);

    const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
        changePaginationPageHook(event, value);
        const response = await dispatch(getQualificationsByPagination(value, filters) as any);
        await handleResponse(response, 'listar calificaciones', false);
    };

    if (!hasPermission) return <Box sx={{ p: 3 }}><Typography>No tienes permisos para ver esta tabla.</Typography></Box>;
    if (!canView) return null;

    return (
        <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 3 }}>
            {ResponseComponents}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControlLabel
                        control={<Switch checked={checked} onChange={(e) => {
                            handleSwitchChange(e);
                            if (onStatusChange) {
                                onStatusChange(e.target.checked);
                            }
                        }} size="small" />}
                        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Mostrar solo inactivas</Typography>}
                    />
                    <Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed', mx: 1 }} />
                    <Chip
                        label={`Activas: ${qualifications_stats?.totalActive || 0}`}
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                    />
                    <Chip
                        label={`Inactivas: ${qualifications_stats?.totalInactive || 0}`}
                        color="error"
                        variant="outlined"
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                    />
                    <Chip
                        label={`Total: ${qualifications_stats?.total || 0}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                    />

                    {qualifications_stats && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Chip size="medium" icon={<span style={{ marginLeft: '6px', fontSize: '22px' }}>😡</span>} label={qualifications_stats.mala || 0} variant="outlined" sx={{ fontWeight: 600, borderColor: 'rgba(0,0,0,0.1)' }} />
                                <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Mala</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Chip size="medium" icon={<span style={{ marginLeft: '6px', fontSize: '22px' }}>😟</span>} label={qualifications_stats.regular || 0} variant="outlined" sx={{ fontWeight: 600, borderColor: 'rgba(0,0,0,0.1)' }} />
                                <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Regular</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Chip size="medium" icon={<span style={{ marginLeft: '6px', fontSize: '22px' }}>😐</span>} label={qualifications_stats.buena || 0} variant="outlined" sx={{ fontWeight: 600, borderColor: 'rgba(0,0,0,0.1)' }} />
                                <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Buena</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Chip size="medium" icon={<span style={{ marginLeft: '6px', fontSize: '22px' }}>🙂</span>} label={qualifications_stats['muy buena'] || 0} variant="outlined" sx={{ fontWeight: 600, borderColor: 'rgba(0,0,0,0.1)' }} />
                                <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Muy Buena</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Chip size="medium" icon={<span style={{ marginLeft: '6px', fontSize: '22px' }}>😍</span>} label={qualifications_stats.excelente || 0} variant="outlined" sx={{ fontWeight: 600, borderColor: 'rgba(0,0,0,0.1)' }} />
                                <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Excelente</Typography>
                            </Box>
                        </Box>
                    )}

                    {(has_discrepancie !== undefined || completion_percentage !== undefined) && (
                        <>
                            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderStyle: 'dashed', mx: 1 }} />
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Tooltip title={has_discrepancie ? "Existen discrepancias en el número de eventos reportados vs la realidad." : "El número de eventos reportados coincide."} placement="top" arrow>
                                        <Chip
                                            size="medium"
                                            icon={has_discrepancie ? <WarningIcon sx={{ fontSize: '18px !important', color: '#FFB400 !important' }} /> : <CheckCircleIcon sx={{ fontSize: '18px !important', color: '#5DCC0A !important' }} />}
                                            label={has_discrepancie ? 'Discrepancia' : 'Concordancia'}
                                            variant="outlined"
                                            sx={{
                                                fontWeight: 700,
                                                borderColor: has_discrepancie ? alpha('#FFB400', 0.5) : alpha('#5DCC0A', 0.5),
                                                bgcolor: has_discrepancie ? alpha('#FFB400', 0.1) : alpha('#5DCC0A', 0.1),
                                                color: has_discrepancie ? '#d39600' : '#4ba804'
                                            }}
                                        />
                                    </Tooltip>
                                    <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Estado de Reporte</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Tooltip title="Porcentaje de encuestas diligenciadas respecto al total esperado." placement="top" arrow>
                                        <Chip
                                            size="medium"
                                            icon={<FactCheckIcon sx={{ fontSize: '18px !important', color: 'primary.main !important' }} />}
                                            label={`${completion_percentage || 0}%`}
                                            variant="outlined"
                                            sx={{
                                                fontWeight: 800,
                                                borderColor: alpha('#3f51b5', 0.5),
                                                bgcolor: alpha('#3f51b5', 0.1),
                                                color: 'primary.main',
                                                minWidth: 70,
                                                justifyContent: 'center'
                                            }}
                                        />
                                    </Tooltip>
                                    <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Diligenciadas</Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            <Box sx={{ position: 'relative' }}>
                    <Box 
                        sx={{ 
                            position: 'fixed', 
                            right: 24, 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            display: isTableVisible ? 'flex' : 'none', 
                            flexDirection: 'column', 
                            gap: 1.5, 
                            zIndex: 1000 
                        }}
                    >
                        <Tooltip title="Desplazar a la izquierda" placement="left">
                            <IconButton size="medium" onClick={() => handleScrollTable('left')} sx={{ bgcolor: 'white', boxShadow: 3, '&:hover': { bgcolor: 'grey.100' } }}>
                                <ArrowLeftIcon fontSize="small" sx={{ ml: 0.5 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Desplazar a la derecha" placement="left">
                            <IconButton size="medium" onClick={() => handleScrollTable('right')} sx={{ bgcolor: 'white', boxShadow: 3, '&:hover': { bgcolor: 'grey.100' } }}>
                                <ArrowRightIcon fontSize="small" sx={{ mr: 0.5 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                <TableContainer ref={handleTableRef} sx={{ pb: 1 }}>
                    <Table sx={{ minWidth: 700 }}>
                        <GenericTableHead 
                            headCells={headCells} 
                            onRequestSort={() => {}} 
                            orderBy={''} 
                            order={'asc'} 
                        />
                        <TableBody>
                            {filteredList.length > 0 ? (
                                filteredList
                                    .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                                    .map((row: any) => (
                                        <TableRow 
                                            hover 
                                            key={row.id}
                                            onContextMenu={(e) => handleContextMenu(e, row)}
                                            sx={{
                                                ...(row.statusCase ? { 
                                                    bgcolor: alpha('#fe464cff', 0.08),
                                                    '&:hover': {
                                                        bgcolor: `${alpha('#f84147ff', 0.12)} !important`
                                                    }
                                                } : {}),
                                                cursor: 'context-menu'
                                            }}
                                        > 
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.clientName}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {row.clientEmail || 'Sin correo'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <ClampedText text={row.comments} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={row.surveyName} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {row.origin?.toLowerCase() === 'google' && (
                                                        <Box component="img" src="/social/google.png" alt="Google" sx={{ width: 20, height: 20, objectFit: 'contain' }} />
                                                    )}
                                                    <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }} color={!row.origin ? 'text.secondary' : 'textPrimary'}>
                                                        {row.origin || 'Sin datos'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {row.deviceName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#2e263d' }}>
                                                        {row.progress && Number(row.progress) > 0 
                                                            ? Math.round(Number(row.progress))
                                                            : row.totalQuestions 
                                                                ? Math.round(((row.answers?.length || 0) / row.totalQuestions) * 100) 
                                                                : 100
                                                        }%
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                        {row.answers?.length || 0}/{row.totalQuestions || row.answers?.length || 0}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip 
                                                    title={
                                                        <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 0.5, pr: 1, minWidth: 260 }}>
                                                            {row.answers && row.answers.length > 0 ? row.answers.map((q: any, idx: number) => (
                                                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1, pb: 1, borderBottom: idx < row.answers.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'rgba(255,255,255,0.5)', mt: 0.2 }}>{idx + 1}.</Typography>
                                                                        <Box>
                                                                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.2 }}>{q.questionText || q.question || q.text}</Typography>
                                                                            {q.metricType && (
                                                                                <Chip label={q.metricType} size="small" sx={{ height: 14, fontSize: '0.55rem', mt: 0.5, bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontWeight: 800 }} />
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                    <Chip label={`${q.score}`} size="small" sx={{ height: 20, minWidth: 28, fontSize: '0.75rem', fontWeight: 800, bgcolor: alpha(getAnswerColor(q.metricType, q.score), 0.15), color: getAnswerColor(q.metricType, q.score), border: '1px solid', borderColor: alpha(getAnswerColor(q.metricType, q.score), 0.3) }} />
                                                                </Box>
                                                            )) : <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>No hay preguntas registradas</Typography>}
                                                        </Box>
                                                    } 
                                                    placement="top" 
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: '#2e263d',
                                                                color: 'white',
                                                                boxShadow: 3,
                                                                maxWidth: 280
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Chip
                                                        icon={<InfoIcon sx={{ fontSize: '16px !important', color: `${getScoreColor(row.averageScore)} !important` }} />}
                                                        label={`${Number(row.averageScore).toFixed(1)}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: getScoreColor(row.averageScore),
                                                            borderColor: getScoreColor(row.averageScore),
                                                            bgcolor: 'transparent',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                bgcolor: alpha(getScoreColor(row.averageScore), 0.1),
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {row.metrics?.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                        {row.metrics.map((m: any, idx: number) => {
                                                            const props = getNPSChipProps(m);
                                                            return (
                                                                <Tooltip title={props.tooltip} key={idx} placement="top" arrow>
                                                                    <Chip 
                                                                        icon={<InfoIcon sx={{ fontSize: '14px !important', color: `${props.color} !important` }} />}
                                                                        label={props.label} 
                                                                        size="small" 
                                                                        sx={{
                                                                            height: 22,
                                                                            fontSize: '0.65rem',
                                                                            fontWeight: 700,
                                                                            color: props.color,
                                                                            bgcolor: props.bgcolor,
                                                                            border: `1px solid ${props.borderColor}`
                                                                        }} 
                                                                    />
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Sin métricas</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PersonIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{row.agentName}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{  formatDateToYmd(row.createdAt)} </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {row.audio ? (
                                                    <AudioPlayer fileName={row.audio} />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Sin audio</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <StatusChip status={row.status} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Tooltip title="Compartir">
                                                        <IconButton color="info" onClick={(e) => handleContextMenu(e, row)}>
                                                            <ShareIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                  
                                                    {row.caseId && (
                                                        <Tooltip title={row.statusCase === 'OPEN' ? "Atender Caso" : "Ver Caso Cerrado"}>
                                                            <IconButton color={row.statusCase === 'OPEN' ? "success" : "primary"} onClick={() => handleViewCase(row)}>
                                                                {row.statusCase === 'OPEN' ? <SupportAgentIcon /> : <LockIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {canDelete && row.status === 1 && (
                                                        <Tooltip title="Eliminar">
                                                            <IconButton color="error" onClick={() => handleDelete(row)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canRestore && row.status === 0 && (
                                                        <Tooltip title="Restaurar">
                                                            <IconButton color="success" onClick={() => handleRestore(row)}>
                                                                <RestoreIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={13} sx={{ borderBottom: 'none' }}>
                                        <SinDatos />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                        color="primary"
                        count={calculatedPageCount}
                        page={pagePagination}
                        onChange={changePaginationPage}
                        showFirstButton
                        showLastButton
                        shape="circular"
                    />
                </Box>

                <ModalConfirmDeactivation 
                    open={openModalDeactivation} 
                    setOpen={setOpenModalDeactivation} 
                    onConfirm={confirmDeactivate} 
                />

                <ModalConfirmRestore 
                    open={openModalRestore} 
                    setOpen={setOpenModalRestore} 
                    onConfirm={confirmRestore} 
                />

                <Menu
                    open={contextMenu !== null}
                    onClose={handleCloseContextMenu}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                    slotProps={{
                        paper: {
                            sx: { boxShadow: 3, minWidth: 200, borderRadius: 2 }
                        }
                    }}
                >
                    <MenuItem onClick={handleDownloadImage} sx={{ gap: 1.5, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                            <DownloadIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Descargar como Imagen</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleShareWhatsAppApp} sx={{ gap: 1.5, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                            <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                        </ListItemIcon>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Enviar por WhatsApp (App)</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleShareWhatsAppWeb} sx={{ gap: 1.5, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                            <WhatsAppIcon fontSize="small" sx={{ color: '#128C7E' }} />
                        </ListItemIcon>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Enviar por WhatsApp Web</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleShareEmail} sx={{ gap: 1.5, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                            <EmailIcon fontSize="small" color="secondary" />
                        </ListItemIcon>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Enviar por Correo</Typography>
                    </MenuItem>
                </Menu>

                <Box sx={{ position: 'fixed', top: -9999, left: -9999, zIndex: -9999 }}>
                    <ShareTicket row={contextMenu?.row} ref={ticketRef} />
                </Box>
            </Box>
        </Card>
    );
};
